import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { credentialRepository } from '../api/credential.repository';
import type { CredentialStatus } from '../model/credential';

export const credentialKeys = {
  all: ['credentials'] as const,
  mine: (userId: string) => [...credentialKeys.all, 'mine', userId] as const,
  pending: () => [...credentialKeys.all, 'pending'] as const,
};

export function useMyCredentials() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: credentialKeys.mine(userId ?? 'anon'),
    queryFn: () => unwrap(credentialRepository.listMine(userId as string)),
    enabled: Boolean(userId),
  });
}

export function useUploadCredential() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (file: File) => unwrap(credentialRepository.upload(userId as string, file)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credentialKeys.mine(userId ?? 'anon') });
    },
  });
}

export function usePendingCredentials() {
  return useQuery({
    queryKey: credentialKeys.pending(),
    queryFn: () => unwrap(credentialRepository.listPending()),
  });
}

export function useReviewCredential() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (vars: { id: string; status: CredentialStatus }) =>
      unwrap(credentialRepository.setStatus(vars.id, vars.status, userId as string)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credentialKeys.pending() });
    },
  });
}

export function openCredential(path: string): void {
  void unwrap(credentialRepository.signedUrl(path)).then((url) => {
    if (url) window.open(url, '_blank', 'noopener');
  });
}
